import React, { useMemo, useState } from "react";
import { publishNewsletter } from "../../../../services/newsletterServices";
import { toOffsetISOString } from "../../../../utils/newsletterDateUtils";
import ContentSectionCard from "../../content/ContentSectionCard";
import NewsletterPublishSummaryDialog from "../NewsletterPublishSummaryDialog";
import NewsletterPublishTemplateSection from "./NewsletterPublishTemplateSection";
import NewsletterScheduleSection, { SCHEDULE_LATER } from "./NewsletterScheduleSection";
import NewsletterCsvImportPanel from "./NewsletterCsvImportPanel";
import NewsletterPublishFormLayout from "./NewsletterPublishFormLayout";
import { parseNewsletterCsv } from "./parseNewsletterCsv";
import { useNewsletterPublishState } from "./useNewsletterPublishState";
import { useNewsletterScheduleState } from "./useNewsletterScheduleState";
import { buildPublishSuccessMessage } from "./newsletterPublishMessages";
import { useNewsletterPublishFeedback } from "./useNewsletterPublishFeedback";

const NewsletterPublishCsvView = () => {
  const notify = useNewsletterPublishFeedback();
  const publishState = useNewsletterPublishState();
  const {
    templatesLoading,
    templateLoading,
    selectedTemplateKey,
    setSelectedTemplateKey,
    subject,
    handleSubjectChange,
    compileErrors,
    subjectWarning,
    templateOptions,
    canPrepare,
    compiledHtml,
  } = publishState;

  const [csvText, setCsvText] = useState("");
  const {
    scheduleMode,
    scheduledAt,
    scheduledAtError,
    handleScheduleModeChange,
    handleScheduledAtChange,
    validateSchedule,
  } = useNewsletterScheduleState();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const { validEmails, invalidLines, duplicateCount } = useMemo(() => parseNewsletterCsv(csvText), [csvText]);

  const summaryRecipients = useMemo(
    () => validEmails.map((email) => ({ email, roleLabel: "Lista" })),
    [validEmails]
  );

  const handlePrepareSend = () => {
    if (!canPrepare) {
      notify({
        title: "Invio non disponibile",
        message: "Seleziona un template valido con oggetto e HTML compilato.",
      });
      return;
    }
    if (!validateSchedule()) {
      return;
    }
    if (!validEmails.length) {
      notify({ title: "Nessun destinatario", message: "Importa almeno una email valida dalla lista." });
      return;
    }
    setSummaryOpen(true);
  };

  const handleConfirmSend = async (emails) => {
    setSending(true);
    try {
      const result = await publishNewsletter({
        emails,
        subject: subject.trim(),
        htmlBody: compiledHtml,
        scheduledAt: scheduleMode === SCHEDULE_LATER ? toOffsetISOString(scheduledAt) : undefined,
      });
      setSummaryOpen(false);
      const msg = buildPublishSuccessMessage(result, scheduleMode);
      notify({ ...msg, detail: result.historyId ? `ID storico: ${result.historyId}` : undefined });
    } catch (err) {
      notify({ title: "Invio non riuscito", message: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <NewsletterPublishFormLayout
        onConfirm={handlePrepareSend}
        confirmDisabled={!canPrepare || !validEmails.length}
      >
        <ContentSectionCard title="Template e oggetto">
          <NewsletterPublishTemplateSection
            templatesLoading={templatesLoading}
            templateLoading={templateLoading}
            selectedTemplateKey={selectedTemplateKey}
            onTemplateChange={setSelectedTemplateKey}
            templateOptions={templateOptions}
            subjectWarning={subjectWarning}
            compileErrors={compileErrors}
            subject={subject}
            onSubjectChange={handleSubjectChange}
          />
        </ContentSectionCard>
        <ContentSectionCard title="Import lista">
          <NewsletterCsvImportPanel
            csvText={csvText}
            onCsvTextChange={setCsvText}
            validEmails={validEmails}
            invalidLines={invalidLines}
            duplicateCount={duplicateCount}
          />
        </ContentSectionCard>
        <ContentSectionCard title="Programmazione">
          <NewsletterScheduleSection
            scheduleMode={scheduleMode}
            onScheduleModeChange={handleScheduleModeChange}
            scheduledAt={scheduledAt}
            onScheduledAtChange={handleScheduledAtChange}
            scheduledAtError={scheduledAtError}
          />
        </ContentSectionCard>
      </NewsletterPublishFormLayout>
      <NewsletterPublishSummaryDialog
        open={summaryOpen}
        recipients={summaryRecipients}
        subject={subject}
        htmlPreview={compiledHtml}
        onClose={() => !sending && setSummaryOpen(false)}
        onConfirm={handleConfirmSend}
        sending={sending}
        scheduledAt={scheduleMode === SCHEDULE_LATER ? toOffsetISOString(scheduledAt) : undefined}
      />
    </>
  );
};

export default NewsletterPublishCsvView;
